import Storage from '../lib/storage';
import { prepareData, displayStatusMessage } from '../helpers/notes-and-favorites';
import { sleep } from '../lib/sleep';
import { IS_ACTIVE, IS_DISABLED, IS_HIDDEN, KEYBOARD } from '../constants';

const BASE_TEXTAREA_HEIGHT = 34;
const MAX_NOTE_LENGTH = 2000;

const attachNoteEvents = (addNoteElements: NodeListOf<HTMLDivElement>, storage: Storage, isShowNoteAfterLogin: boolean): void => {
    addNoteElements.forEach((element: HTMLDivElement) => {
        const noteElement: HTMLDivElement | null = element.querySelector('.js-editable-container');
        const noteWrapper: HTMLDivElement | null = element.querySelector('.js-note-wrap');
        const placeholder: HTMLDivElement | null = element.querySelector('.js-placeholder');

        if (noteElement && noteWrapper && placeholder) {
            attachGrowTextAreaEvents(noteElement, placeholder, noteWrapper, element);

            if (isShowNoteAfterLogin) {
                noteWrapper.classList.add(IS_ACTIVE);
                noteElement.click();
                noteElement.focus();

                storage.delete('showNotesAfterLogin');

                Analytics.sendEvent({
                    category: 'Profile',
                    action: 'Note',
                    label: 'Guest logged in',
                });
            }
        }
    });
};

const attachGrowTextAreaEvents = (noteElement: HTMLDivElement, placeholder: HTMLDivElement, noteWrapper: HTMLDivElement, element: HTMLDivElement) => {
    let prevContent = '';
    let prevHeight = BASE_TEXTAREA_HEIGHT;
    let running = false;
    const startTopPosition = noteWrapper.offsetTop;

    noteWrapper.addEventListener('click', () => {
        noteElement.focus();
    });

    noteWrapper.addEventListener('mouseenter', () => {
        prevHeight = validatePosition(noteWrapper, prevHeight, startTopPosition);
    });

    noteWrapper.addEventListener('mouseleave', () => {
        if (!noteWrapper.classList.contains(IS_ACTIVE)) {
            noteElement.scrollTo(0, 0);
            noteWrapper.style.top = `${startTopPosition}px`;
        }
    });

    noteElement.addEventListener('blur', async () => {
        const note = noteElement.innerText.trim();
        noteElement.scrollTo(0, 0);
        noteWrapper.classList.remove(IS_ACTIVE);
        noteWrapper.style.top = `${startTopPosition}px`;

        if (note.length === 0) {
            noteElement.innerText = '';
            placeholder.classList.remove(IS_HIDDEN);
        }

        if (prevContent !== note) {
            if (running) {
                return;
            }

            const chathostID: string | undefined = element.dataset.id;
            const action: string | undefined = element.dataset.action;
            const chathostNick: string | undefined = element.dataset.name;

            if (chathostID && action && chathostNick && noteWrapper) {
                running = true;
                noteWrapper.classList.add(IS_DISABLED);
                validateContentClass(note.length > 0, noteWrapper);
                const requestStatus: boolean = await prepareData(chathostID, action, { note: note });
                displayStatusMessage({
                    chathost: chathostNick,
                    action: action,
                    status: requestStatus,
                });
                await sleep(500);
                noteElement.innerHTML = note;
                noteWrapper.classList.remove(IS_DISABLED);
                running = false;
            }
        }
    });

    noteElement.addEventListener('focus', () => {
        prevContent = noteElement.innerText.trim();
        noteWrapper.classList.add(IS_ACTIVE);
        placeholder.classList.add(IS_HIDDEN);
    });

    noteElement.addEventListener('keydown', (e: KeyboardEvent) => {
        if (noteElement.innerText.length >= MAX_NOTE_LENGTH && e.key !== KEYBOARD.BACKSPACE) {
            e.preventDefault();
        }
    });

    noteElement.addEventListener('input', () => {
        prevHeight = validatePosition(noteWrapper, prevHeight, startTopPosition);
    });

    noteElement.addEventListener('paste', (e: ClipboardEvent) => {
        e.preventDefault();

        const text: string | undefined = e.clipboardData?.getData('text/plain');

        if (text) {
            // eslint-disable-next-line
            document.execCommand('insertText', false, text);
            noteElement.innerText = noteElement.innerText.trim().slice(0, MAX_NOTE_LENGTH);
        }
    });
};

const validateContentClass = (hasContent: boolean, contentDiv: HTMLDivElement) => {
    if (hasContent) {
        contentDiv.classList.add('has-content');
    } else {
        contentDiv.classList.remove('has-content');
    }
};

const validatePosition = (element: HTMLElement, prevHeight: number, startTopPosition: number): number => {
    const boundRect: DOMRect = element.getBoundingClientRect();
    if (boundRect.y + boundRect.height + 10 > window.innerHeight || (prevHeight > boundRect.height && element.offsetTop !== startTopPosition)) {
        element.style.top = `-${boundRect.height - BASE_TEXTAREA_HEIGHT}px`;
    }
    return boundRect.height;
};

const openLoginModal = (addNoteNotLoggedIn: HTMLElement, storage: Storage): void => {
    const btnLinkNote: HTMLDivElement | null = addNoteNotLoggedIn.querySelector('.js-note-open-modal');
    addNoteNotLoggedIn.addEventListener('click', () => {
        if (btnLinkNote) {
            btnLinkNote.click();
            storage.set('showNotesAfterLogin', true);
        }
    });
};

const attachEvents = () => {
    const storage: Storage = new Storage('notes');
    const addNoteNotLoggedIn: HTMLDivElement | null = document.querySelector('.js-add-note-set');
    const addNoteElements: NodeListOf<HTMLDivElement> = document.querySelectorAll('.js-add-note');
    const isShowNoteAfterLogin: boolean = storage.get('showNotesAfterLogin');

    if (addNoteNotLoggedIn) {
        openLoginModal(addNoteNotLoggedIn, storage);
    }

    attachNoteEvents(addNoteElements, storage, isShowNoteAfterLogin);
};

attachEvents();
